import { useState, useEffect } from 'react';
import branchService from '../services/branchService';

// Cache branch data to avoid repeated API calls
let cachedBranch = null;
let cachePromise = null;

/**
 * Hook to fetch and cache branch data
 * Uses singleton pattern to avoid multiple API calls
 *
 * @returns {Object} { branch, loading, error, refetch }
 */
export default function useBranch() {
  const [branch, setBranch] = useState(cachedBranch);
  const [loading, setLoading] = useState(!cachedBranch);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBranch = async () => {
      // If already cached, use it
      if (cachedBranch) {
        setBranch(cachedBranch);
        setLoading(false);
        return;
      }

      // If a fetch is already in progress, wait for it
      if (cachePromise) {
        try {
          const data = await cachePromise;
          setBranch(data);
          setLoading(false);
        } catch (err) {
          setError(err.message);
          setLoading(false);
        }
        return;
      }

      // Start new fetch
      setLoading(true);
      cachePromise = branchService.getCurrentBranch();

      try {
        const data = await cachePromise;
        cachedBranch = data;
        setBranch(data);
        setError(null);
      } catch (err) {
        console.error('Failed to fetch branch:', err);
        setError(err.message);
        // Fallback to generic name
        cachedBranch = { name: 'Organization' };
        setBranch(cachedBranch);
      } finally {
        setLoading(false);
        cachePromise = null;
      }
    };

    fetchBranch();
  }, []);

  // Force refetch (clears cache)
  const refetch = async () => {
    cachedBranch = null;
    cachePromise = null;
    setLoading(true);

    try {
      const data = await branchService.getCurrentBranch();
      cachedBranch = data;
      setBranch(data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return {
    branch,
    branchName: branch?.name || 'Organization',
    timezone: branch?.timezone || 'Asia/Kolkata',
    loading,
    error,
    refetch
  };
}

/**
 * Clear branch cache (call on logout)
 */
export function clearBranchCache() {
  cachedBranch = null;
  cachePromise = null;
}
